import React, { useState, useRef, useEffect } from "react";
import InnoIcons from "@InnoIcons/";
import SC from "./style";

const TextFilterDropdown = ({
  label,
  headerOptionsValuename = "File Name",
  selectedHeaderId,
  value = "",
  onHeaderChange,
  onUpdate,
  isEmptyOption = false,
  pointIdOption = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const dropdownRef = useRef();

  // markers used when applying empty/not-empty mode
  const IS_EMPTY_MARKER = "__IS_EMPTY__";
  const IS_NOT_EMPTY_MARKER = "__IS_NOT_EMPTY__";

  const baseHeaderOptions = [
    { id: "equals", label: `${headerOptionsValuename} = (equals)` },
    { id: "not-equals", label: `${headerOptionsValuename} != (not equals)` },
    { id: "contains", label: `${headerOptionsValuename} ~ contains` },
    { id: "startsWith", label: `${headerOptionsValuename} starts with` },
    { id: "endsWith", label: `${headerOptionsValuename} ends with` },
  ];

  // const headerOptions = [
  //   ...baseHeaderOptions,
  //   ...(isEmptyOption
  //     ? [
  //         { id: "is-empty", label: `${headerOptionsValuename} IS EMPTY` },
  //         {
  //           id: "is-not-empty",
  //           label: `${headerOptionsValuename} IS NOT EMPTY`,
  //         },
  //       ]
  //     : []),
  // ];

  const headerOptions =
    pointIdOption && isEmptyOption
      ? [
          { id: "is-empty", label: `${headerOptionsValuename} IS EMPTY` },
          {
            id: "is-not-empty",
            label: `${headerOptionsValuename} IS NOT EMPTY`,
          },
        ]
      : [
          ...baseHeaderOptions,
          ...(isEmptyOption
            ? [
                { id: "is-empty", label: `${headerOptionsValuename} IS EMPTY` },
                {
                  id: "is-not-empty",
                  label: `${headerOptionsValuename} IS NOT EMPTY`,
                },
              ]
            : []),
        ];

  const selectedHeader = headerOptions.find((h) => h.id === selectedHeaderId);

  // whether input should be hidden/disabled (empty mode)
  const isEmptyMode =
    selectedHeaderId === "is-empty" || selectedHeaderId === "is-not-empty";

  // Determine whether the dropdown button should be highlighted (active)
  // Active if:
  //  - there is a non-blank value,
  //  - OR the selected header indicates empty/not-empty mode,
  //  - OR value equals the special markers (in case parent stores them)
  const hasValueText = typeof value === "string" && value.trim() !== "";
  const isMarkerValue =
    value === IS_EMPTY_MARKER || value === IS_NOT_EMPTY_MARKER;
  const isActive =
    hasValueText ||
    isMarkerValue ||
    selectedHeaderId === "is-empty" ||
    selectedHeaderId === "is-not-empty";

  const handleHeaderOptionClick = (headerId) => {
    if (onHeaderChange) onHeaderChange(headerId);
    setIsHeaderExpanded(false);
  };

  const handleUpdate = () => {
    // If the user selected IS EMPTY / IS NOT EMPTY we send a special marker value
    if (selectedHeaderId === "is-empty") {
      if (onUpdate)
        onUpdate({ headerId: selectedHeaderId, value: IS_EMPTY_MARKER });
    } else if (selectedHeaderId === "is-not-empty") {
      if (onUpdate)
        onUpdate({ headerId: selectedHeaderId, value: IS_NOT_EMPTY_MARKER });
    } else {
      if (onUpdate) onUpdate({ headerId: selectedHeaderId, value: inputValue });
    }
    setIsOpen(false);
  };

  // keep inputValue in sync when parent updates value
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsHeaderExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset header + value to default when clearing
  const handleClear = () => {
    const defaultHeader = "equals"; // change default here if needed
    setInputValue("");
    if (onHeaderChange) onHeaderChange(defaultHeader);
    if (onUpdate) onUpdate({ headerId: defaultHeader, value: "" });
    setIsOpen(false);
  };

  // Build the button label: if empty mode show short status, otherwise show label or typed value
  const renderButtonLabel = () => {
    if (selectedHeaderId === "is-empty" || value === IS_EMPTY_MARKER) {
      return `${label}: IS EMPTY`;
    }
    if (selectedHeaderId === "is-not-empty" || value === IS_NOT_EMPTY_MARKER) {
      return `${label}: IS NOT EMPTY`;
    }
    if (hasValueText) {
      const symbol =
        selectedHeader?.id === "equals"
          ? "="
          : selectedHeader?.id === "not-equals"
            ? "!="
            : selectedHeader?.id === "contains"
              ? "~"
              : selectedHeader?.id === "startsWith"
                ? "starts with"
                : selectedHeader?.id === "endsWith"
                  ? "ends with"
                  : "";
      return `${label}: ${symbol} ${value}`;
    }
    return label;
  };

  return (
    <SC.DropdownContainer ref={dropdownRef}>
      <SC.DropdownButton
        isOpen={isOpen}
        isActive={isActive}
        onClick={() => setIsOpen(!isOpen)}
      >
        {renderButtonLabel()}
        <SC.ChevronIcon isOpen={isOpen} isActive={isActive} />
      </SC.DropdownButton>

      <SC.DropdownMenu isOpen={isOpen}>
        <SC.DropdownHeader
          isClickable
          onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
        >
          <SC.HeaderText>
            {selectedHeader?.label || headerOptions[0].label}
          </SC.HeaderText>
          <SC.HeaderChevron isExpanded={isHeaderExpanded} />
        </SC.DropdownHeader>

        <SC.HeaderOptionsContainer isVisible={isHeaderExpanded}>
          {headerOptions.map((header) => (
            <SC.HeaderOption
              key={header.id}
              isSelected={header.id === selectedHeaderId}
              onClick={() => handleHeaderOptionClick(header.id)}
            >
              {header.label}
            </SC.HeaderOption>
          ))}
        </SC.HeaderOptionsContainer>

        <SC.SearchContainer>
          {/* Hide input if pointIdOption is enabled */}
          {pointIdOption ? (
            <SC.EmptyModeNote>
              {selectedHeaderId === "is-empty"
                ? "Showing files WITHOUT Point ID"
                : "Showing files WITH Point ID"}
            </SC.EmptyModeNote>
          ) : !isEmptyMode ? (
            <SC.SearchInput
              type="text"
              placeholder="Type to search"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          ) : (
            <SC.EmptyModeNote>
              {selectedHeaderId === "is-empty"
                ? "Searching for EMPTY values"
                : "Searching for NON-EMPTY values"}
            </SC.EmptyModeNote>
          )}
        </SC.SearchContainer>

        <SC.DropdownFooter>
          <SC.UpdateButton onClick={handleUpdate}>Update</SC.UpdateButton>
          <SC.ClearButton onClick={handleClear}>Clear</SC.ClearButton>
        </SC.DropdownFooter>
      </SC.DropdownMenu>
    </SC.DropdownContainer>
  );
};

export default TextFilterDropdown;
